import { selectItemById } from '@data/selectors/itemSelectors';

import AppDialog from '@components/ui/AppDialog';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { formatRegistrationPlate, getVehicleFullName } from '@utils/vehicle';
import {
  BodyText,
  Button,
  DeleteIcon,
  Flex,
  Message,
  theme,
} from '@kvdbil/components';
import ItemCard from '../components/ItemCard';
import { vehicleTypeChipSwitch } from '../components/VehicleTypeChipSwitch';
import DefaultSpinner from '@components/ui/DefaultSpinner';
import { useEffect, useState } from 'react';
import { useAppDispatch } from '@hooks/reduxHooks.ts';
import { fetchDeliveryReceiptDocumentThunk } from '@data/actions/deliverReceiptDocumentActions.ts';
import { deliveryReceiptDocumentSelector } from '@data/selectors/deliveryReceiptDocumentSelectors.ts';
import { deliveryReceiptSelector } from '@data/selectors/deliveryReceiptSelectors.ts';
import { PdfViewer } from '../components/PdfViewer.tsx';
import { removeGeneratedReceipt } from '@api/GenerateReceipt.ts';
import { resetDeliveryReceipt } from '@data/slices/deliveryReceiptSlice.ts';
import { resetDeliveryReceiptDocument } from '@data/slices/deliveryReceiptDocumentSlice.ts';
import ItemHeader from '../Item/components/ItemHeader.tsx';

const Container = styled.div`
  overflow: initial;
  display: flex;
  flex-direction: column;
  background: ${theme.colors.background.gray};
`;

const PdfContainer = styled.div`
  padding: 0.5rem;
`;

const StyledBodyText = styled(BodyText)`
  padding: 1rem 0 2rem;
`;

const RemoveButton = styled.div`
  cursor: pointer;
  &:hover {
    color: ${theme.colors.error.main};
  }
`;

const GeneratedReceiptView = () => {
  const { itemId } = useParams<{
    itemId: string;
  }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const [removeDialog, setRemoveDialog] = useState(false);
  const handleRemoveConfirmation = async () => {
    if (itemId) {
      await removeGeneratedReceipt(itemId).then(async () => {
        dispatch(resetDeliveryReceipt());
        dispatch(resetDeliveryReceiptDocument());
        navigate(`/delivery-receipt/${itemId}`);
      });
    }
  };

  const item = useSelector(selectItemById(itemId));
  const deliveryReceipt = useSelector(deliveryReceiptSelector());

  useEffect(() => {
    if (itemId && deliveryReceipt?.receipt) {
      dispatch(
        fetchDeliveryReceiptDocumentThunk({
          processObjectId: deliveryReceipt.receipt.processObjectId,
          documentType: deliveryReceipt.receipt.documentType,
          fileName: deliveryReceipt.receipt.fileName,
        })
      );
    }
  }, [deliveryReceipt.receipt, dispatch, itemId]);

  const deliveryReceiptDocument = useSelector(
    deliveryReceiptDocumentSelector()
  );

  useEffect(() => {
    if (itemId && !deliveryReceipt.isLoading && !deliveryReceipt?.receipt) {
      navigate(`/delivery-receipt/${itemId}`);
    }
  }, [
    deliveryReceipt.isLoading,
    deliveryReceipt?.receipt,
    dispatch,
    itemId,
    navigate,
  ]);

  const formattedRegNr = formatRegistrationPlate(
    item?.properties?.registrationPlate ?? ''
  );

  const subTitle = item ? getVehicleFullName(item) : '-----';
  const title = `${item?.properties?.registrationPlate ? formattedRegNr + ' | ' : ''}${item?.id}`;
  const chipsData = item ? [vehicleTypeChipSwitch(t, item.vehicleType)] : [];

  const removeButton = () => {
    if (item && ['COMPLETED', 'FAILED'].includes(item.state)) {
      return;
    }
    return (
      <RemoveButton
        onClick={() => setRemoveDialog(true)}
        data-testid="remove-button"
      >
        <DeleteIcon width={24} height={24} />
      </RemoveButton>
    );
  };

  if (!item || deliveryReceiptDocument.isLoading || deliveryReceipt.isLoading) {
    return (
      <Flex grow={1}>
        <DefaultSpinner />
      </Flex>
    );
  }
  if (!deliveryReceiptDocument.document) {
    return (
      <Message fullWidth={true} type={'error'} withIcon={true} textCenter>
        {t(
          "We can't display delivery receipt. Please reload the page or contact support."
        )}
      </Message>
    );
  }

  return (
    <Container>
      <ItemHeader
        previewPageButtonHandler={() => {
          navigate('/delivery-receipt');
        }}
        title={t('Delivery receipt')}
        action={removeButton()}
      />
      <ItemCard
        itemId={item.id}
        title={title}
        subTitle={subTitle}
        chips={chipsData}
      />
      <PdfContainer>
        <PdfViewer base64String={deliveryReceiptDocument.document} />
      </PdfContainer>

      <AppDialog
        onClose={() => setRemoveDialog(false)}
        actions={
          <>
            <Button
              color={'neutral'}
              variant={'flat'}
              onClick={() => setRemoveDialog(false)}
            >
              {t('Cancel')}
            </Button>
            <Button
              data-testid="remove-confirmation-button"
              color={'error'}
              onClick={handleRemoveConfirmation}
              prefixIcon={<DeleteIcon />}
            >
              {t('Remove')}
            </Button>
          </>
        }
        isOpen={removeDialog}
        title={t('Remove the Delivery Receipt')}
        withPortal
      >
        <StyledBodyText>
          {t('The delivery receipt will be deleted. This cannot be undone.')}
        </StyledBodyText>
      </AppDialog>
    </Container>
  );
};

export default GeneratedReceiptView;
